import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { ARCHIVAL_MEDIA, RIO_PIEDRAS_SUBMITTED_PHOTOS, archiveLinkForPlace } from "@/data/historyArchive";
import { HISTORY_PLACES } from "@/data/historyPlaces";

const documented = [
  { imageId: "market1942", placeId: "history-rio-piedras" },
  { imageId: "plaza1920", placeId: "history-plaza-convalecencia" },
  { imageId: "university1939", placeId: "history-upr-rio-piedras" },
  { imageId: "university1920", placeId: "history-upr-rio-piedras" },
].map(({ imageId, placeId }) => {
  const media = ARCHIVAL_MEDIA[imageId];
  const place = HISTORY_PLACES.find((item) => item.id === placeId);
  return media?.rightsStatus === "usable" && place
    ? { id: imageId, src: media.src, en: media.titleEn, es: media.titleEs,
        date: media.date, credit: media.credit, url: archiveLinkForPlace(place),
        documented: true }
    : null;
}).filter(Boolean);

const submitted = RIO_PIEDRAS_SUBMITTED_PHOTOS.map((image) => ({
  id: image.id, src: image.src, en: image.titleEn, es: image.titleEs,
  date: null, credit: null, url: "/RioPiedras", documented: false
}));

// Lead with sourced archival records; include submitted photos with honest captions.
const slides = [
  documented[0], documented[1], submitted[1], submitted[3],
  documented[2], submitted[0], submitted[2], documented[3]
].filter(Boolean);

export default function RioPiedrasStorySlideshow() {
  const { lang } = useLang();
  const es = lang === "es";
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);

  useEffect(() => {
    if (slides.length < 2 || paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches)
      return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) setActive((index) => (index + 1) % slides.length);
    }, 6800);
    return () => window.clearInterval(timer);
  }, [paused]);
  const step = (direction) => setActive((index) => (index + direction + slides.length) % slides.length);
  const selected = slides[active];
  if (!selected) return null;

  return (
    <section aria-label={es ? "Río Piedras a través del tiempo" : "Río Piedras through the years"}
      className="mx-4 mt-6 overflow-hidden border border-[#d4d0c7] bg-[#f7f5ef] text-[#171715] dark:border-white/20 dark:bg-[#212023] dark:text-white sm:mx-6 sm:mt-9 lg:mx-auto lg:max-w-[1180px]">
      <header className="flex flex-wrap items-end justify-between gap-3 px-3 py-3 sm:px-6 sm:py-5">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[.12em] text-[#a83b34] dark:text-[#ffafa4]">
            {es ? "EL ARCHIVO / RÍO PIEDRAS" : "THE ARCHIVE / RÍO PIEDRAS"}
          </p>
          <h2 className="mt-1 text-[clamp(27px,5vw,47px)] font-extrabold leading-[1.03] tracking-[-.034em]">
            {es ? "Río Piedras a través del tiempo." : "Río Piedras, through the years."}
          </h2>
        </div>
        <Link to="/magazine"
          className="inline-flex min-h-9 items-center gap-1.5 border-b border-current text-[11px] font-bold transition hover:opacity-65">
          {es ? "Leer historias del barrio" : "Read the archive"} <ArrowUpRight size={15} aria-hidden="true" />
        </Link>
      </header>

      <div
        className="group relative aspect-[4/3] w-full overflow-hidden bg-[#272522] sm:aspect-[16/8] lg:aspect-[16/7]"
        role="region" aria-roledescription={es ? "presentación de fotos" : "slideshow"}
        aria-label={es ? "Imágenes históricas de Río Piedras" : "Río Piedras historical photo collection"}
        tabIndex={0}
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)} onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            step(event.key === "ArrowRight" ? 1 : -1);
          }
        }}
        onTouchStart={(event) => setTouchStart(event.touches[0]?.clientX ?? null)}
        onTouchEnd={(event) => {
          if (touchStart == null) return;
          const delta = event.changedTouches[0]?.clientX - touchStart;
          if (Math.abs(delta) > 50) step(delta < 0 ? 1 : -1);
          setTouchStart(null);
        }}
      >
        <img key={selected.id} src={selected.src}
          alt={es ? selected.es : selected.en}
          loading="lazy" decoding="async"
          className="h-full w-full animate-[yts-hero-fade_400ms_ease-out] object-contain grayscale-[95%]" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />
        <span className="absolute bottom-3 left-3 bg-black/75 px-2 py-1 font-mono text-[10px] font-bold tracking-[.04em] text-white sm:left-5">
          {String(active + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
        </span>
        <div className="absolute bottom-3 right-3 flex gap-1.5 sm:right-5">
          <button type="button" onClick={() => step(-1)}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/40 bg-black/65 text-white transition hover:bg-black focus-visible:ring-2 focus-visible:ring-white"
            aria-label={es ? "Foto anterior" : "Previous photograph"}>
            <ChevronLeft size={20} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => step(1)}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/40 bg-black/65 text-white transition hover:bg-black focus-visible:ring-2 focus-visible:ring-white"
            aria-label={es ? "Siguiente foto" : "Next photograph"}>
            <ChevronRight size={20} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-3 px-3 py-3 sm:px-6 sm:py-4" aria-live="polite">
        <div className="min-w-0">
          <p className="text-[14px] font-bold leading-[1.3] sm:text-[17px]">{es ? selected.es : selected.en}</p>
          <p className="mt-0.5 text-[10px] leading-[1.5] text-[#756f67] dark:text-white/60">
            {selected.documented
              ? `${selected.date} · ${selected.credit}`
              : es ? "Fotografía aportada · fecha y autoría por verificar" : "Submitted photograph · date and photographer unverified"}
          </p>
          {selected.documented && (
            <Link to={selected.url} className="mt-1.5 inline-flex min-h-8 items-center gap-1 text-[11px] font-bold text-[#a63932] underline underline-offset-4 dark:text-[#ffb2a7]">
              {es ? "Leer esta historia" : "Read this story"} <ArrowUpRight size={13} aria-hidden="true" />
            </Link>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {slides.map((slide, index) => (
            <button key={slide.id} type="button" onClick={() => setActive(index)}
              aria-label={es ? `Ver foto ${index + 1}` : `Show photograph ${index + 1}`}
              aria-current={index === active ? "true" : undefined}
              className={`h-2.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#bd3a34] ${index === active ? "w-6 bg-[#b23a32]" : "w-2.5 bg-[#bbb6aa] dark:bg-white/35"}`} />
          ))}
        </div>
      </div>
    </section>
  );
}
